import { Component, ElementRef, HostListener, Inject, OnChanges, PLATFORM_ID, signal, SimpleChanges, ViewChild } from '@angular/core'
import { isPlatformBrowser } from '@angular/common'
import { ElasticsearchService } from "./elasticsearch.service"
import { debounceTime, Subject } from "rxjs"
import { ProductSearchResultItemInterface, WagtailSearchResultItemInterface } from "./models"
import { ScreenSizeService } from "../services/screen-size.service"
import { ProductCollection, ProductFactory } from "@gea-waldviertler-website/gea-models"

@Component({
    selector: 'gea-shop-search-field',
    templateUrl: './search-field.component.html',
    styleUrls: ['./search-field.component.scss'],
    standalone: false
})
export class SearchFieldComponent implements OnChanges {
  productCollection?: ProductCollection
  keyboardNavigationIndex = signal<number>(-1)
  cmsSearchResults?: WagtailSearchResultItemInterface[]
  callSelectionIndex?: number
  // zoneless: template-bound state written from subjects / http callbacks lives in signals
  hasResults = signal<boolean>(false)

  private keyboardNavigationIndexSubject: Subject<number>
  private productCollectionSubject: Subject<ProductCollection | undefined>
  private cmsSearchResultsSubject: Subject<WagtailSearchResultItemInterface[] | undefined>
  private callSelectionIndexSubject: Subject<number>
  private toggleSearchSubject: Subject<boolean>

  private swordSubject: Subject<string>
  public sword: string = ""
  public visible = signal<boolean>(false)
  public searching = signal<boolean>(false)

  private itemsPerRow = {
    xs: 1, sm: 1, md: 2, lg: 3, xl: 4, xxl: 4
  }

  private static readonly IGNORED_KEYS = ['Shift', 'Control', 'Alt', 'Meta', 'Tab', 'CapsLock']

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>

  constructor(
    @Inject(ElasticsearchService) private es: ElasticsearchService,
    @Inject(ScreenSizeService) private ssService: ScreenSizeService,
    private elementRef: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {

    this.keyboardNavigationIndexSubject = this.es.getKeyboardNavigationIndex()
    this.keyboardNavigationIndexSubject.subscribe(v => {
      this.keyboardNavigationIndex.set(v)
    })

    this.productCollectionSubject = this.es.getProductCollection()
    this.productCollectionSubject.subscribe(c => {
      this.productCollection = c
      this.hasResults.set(!! (c && c.size))
    })

    this.cmsSearchResultsSubject = this.es.getCmsSearchResults()
    this.cmsSearchResultsSubject.subscribe(r => {
      this.cmsSearchResults = r
    })

    this.callSelectionIndexSubject = this.es.getCallSelectionEvent()
    this.callSelectionIndexSubject.subscribe(i => {
      this.callSelectionIndex = i
    })

    this.toggleSearchSubject = this.es.getToggleSearchSubject()
    this.toggleSearchSubject.subscribe(force => {
      this.toggle(force)
    })

    this.productCollection = new ProductCollection()
    this.swordSubject = new Subject<string>()

    this.swordSubject.pipe(debounceTime(400)).subscribe((sword: string) => {
      if (! this.visible()) return
      if (this.sword.length < 2) return
      this.searching.set(true)

      this.productCollection?.clear()
      this.productCollectionSubject.next(this.productCollection)
      this.cmsSearchResults = []
      this.cmsSearchResultsSubject.next(this.cmsSearchResults)

      this.es.query(this.sword.trim()).subscribe(result => {
        if (! this.cmsSearchResults) this.cmsSearchResults = []
        if (! this.productCollection) this.productCollection = new ProductCollection()
        for (const hit of result.hits.hits) {
          if (hit._index.startsWith("cms-pages")) {
            this.cmsSearchResults.push(hit._source as WagtailSearchResultItemInterface)
          } else if (hit._index.startsWith("shop-products")) {
            const source = hit._source as ProductSearchResultItemInterface
            this.productCollection.add(ProductFactory.getTypeInstance(JSON.parse(source.productJson)))
          }
        }
        this.productCollectionSubject.next(this.productCollection)
        this.cmsSearchResultsSubject.next(this.cmsSearchResults)
        this.searching.set(false)
      }, error => {
        this.searching.set(false)
      })
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  private getAllItemsSize() {
    const pcsize = this.productCollection ? this.productCollection.size : 0
    const cmsize = this.cmsSearchResults ? this.cmsSearchResults.length : 0
    return pcsize + cmsize
  }

  private shouldTriggerSearch(e: KeyboardEvent): boolean {
    if (! this.visible()) return false
    if (! e.key) return false
    return ! SearchFieldComponent.IGNORED_KEYS.includes(e.key)
  }

  onKeyUp(e: KeyboardEvent) {
    if (! this.shouldTriggerSearch(e)) return
    const itemsPerRow = this.itemsPerRow[this.ssService.getCurrentDimension()]
    if (e.key == "Escape") {
      this.toggle()
    } else if (e.key == 'ArrowDown' || e.key == 'ArrowRight' || e.key == 'ArrowUp' || e.key == 'ArrowLeft') {
      let index = this.keyboardNavigationIndex()
      if (e.key == 'ArrowDown') {
        if (index == -1) {
          index = 0
        } else if (index + itemsPerRow < this.getAllItemsSize()) {
          index = index + itemsPerRow
        } else {
          index = this.getAllItemsSize() - 1
        }
      } else if (e.key == 'ArrowRight') {
        if (index == -1) return
        if (index < this.getAllItemsSize() - 1) {
          index++
        }
      } else if (e.key == 'ArrowUp') {
        if (index > -1) {
          index = index - itemsPerRow
          if (index < 0) index = -1
        }
      } else if (e.key == 'ArrowLeft') {
        if (index > -1) {
          index--
        }
      }
      this.keyboardNavigationIndex.set(index)
      this.keyboardNavigationIndexSubject.next(index)

    } else if (e.key == 'Enter') {
      if (this.keyboardNavigationIndex() > -1) {
        this.callSelectionIndexSubject.next(this.keyboardNavigationIndex())
        this.toggle()
      }
    } else {
      this.keyboardNavigationIndex.set(-1)
      this.keyboardNavigationIndexSubject.next(-1)
      this.swordSubject.next(this.sword)
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (! isPlatformBrowser(this.platformId)) return
    if (! this.visible()) return

    const target = event.target as Node | null
    if (! target) return

    if (this.elementRef.nativeElement.contains(target)) return

    const element = target instanceof Element ? target : target.parentElement
    if (element && typeof element.closest === 'function' && element.closest('gea-shop-search-results')) return

    this.toggle(false)
  }

  toggle(force?: boolean) {
    if (force !== undefined) {
      this.visible.set(force)
    } else {
      this.visible.update(visible => ! visible)
    }

    if (this.visible()) {
      setTimeout(() => {this.searchInput?.nativeElement.focus()}, 70)
    } else {
      this.clear()
    }
  }

  clear() {
    this.sword = ''
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = ''
    }
    this.swordSubject.next('')
    this.productCollection?.clear()
    this.productCollection = undefined
    this.cmsSearchResults = undefined
    this.hasResults.set(false)
    this.searching.set(false)
    this.keyboardNavigationIndex.set(-1)
    this.keyboardNavigationIndexSubject.next(-1)
    this.productCollectionSubject.next(undefined)
    this.cmsSearchResultsSubject.next(undefined)
  }
}
